// What a linked browser sees of one conversation (docs/07): segments with the CEK this
// device could or could not unwrap, and events whose content is plaintext once the CEK is
// known, locked while it is not, or failed when the ciphertext does not authenticate.
import type { Cek } from "../device/crypto";
import type { Direction, EncryptedEvent, EventType, SegmentOpen } from "../proto/frames";

export type SegmentKey = { kind: "pending" } | { kind: "missing" } | { kind: "unwrapped"; cek: Cek };

export interface SegmentView {
  /** ULID string. */
  id: string;
  open: SegmentOpen;
  key: SegmentKey;
  /** The worker's final event count, once `segment.close` arrived. */
  closedWith?: number;
}

export type EventContent =
  | { kind: "plaintext"; text: string }
  | { kind: "locked" }
  | { kind: "failed"; reason: string };

export interface EventView {
  segmentId: string;
  seq: number;
  type: EventType;
  direction: Direction;
  /** The plaintext metadata JSON the worker sent, parsed; a string when it is not JSON. */
  meta: unknown;
  content: EventContent;
  /** The ciphertext as relayed, kept so a CEK that arrives later can still open it. */
  raw: EncryptedEvent;
}

export interface ConversationState {
  segments: Map<string, SegmentView>;
  /** Ordered as received: replay first, then live. */
  events: EventView[];
  /** Whether a worker is connected for this conversation right now. */
  live: boolean;
}

export const emptyConversation = (): ConversationState => ({ segments: new Map(), events: [], live: false });

/** The newest segment whose CEK this device holds: where an answer or instruction goes. */
export function writableSegment(state: ConversationState): (SegmentView & { key: { kind: "unwrapped"; cek: Cek } }) | undefined {
  const open = Array.from(state.segments.values())
    .filter((s): s is SegmentView & { key: { kind: "unwrapped"; cek: Cek } } => s.key.kind === "unwrapped")
    .filter((s) => s.closedWith === undefined);
  return open.at(-1);
}

/** That segment, when it is open and this device holds its CEK. */
export function writableSegmentById(state: ConversationState, id: string): ReturnType<typeof writableSegment> {
  const s = state.segments.get(id);
  return s && s.key.kind === "unwrapped" && s.closedWith === undefined ? (s as ReturnType<typeof writableSegment>) : undefined;
}

/** The device's next inbound seq in a segment: one past its own messages so far. */
export function nextInboundSeq(state: ConversationState, segmentId: string): number {
  const mine = state.events.filter((e) => e.segmentId === segmentId && e.direction === "inbound").map((e) => e.seq);
  return mine.length === 0 ? 1 : Math.max(...mine) + 1;
}

/**
 * The plaintext of an `answer` event: `{"question_id", "answer"}`, the contract ocs-capture
 * reads (crates/ocs-capture/src/domain/question.rs); a bare answer is refused there.
 */
export function answerContent(questionId: string, answer: string): string {
  return JSON.stringify({ question_id: questionId, answer });
}

/** What to show for an event's plaintext: an answer's text, not its JSON envelope. */
export function displayText(type: EventType, text: string): string {
  if (type === "instruction") return chatPromptText(text) ?? text;
  if (type !== "answer") return text;
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed && typeof parsed === "object" && "answer" in parsed && typeof parsed.answer === "string") {
      return parsed.answer;
    }
  } catch {
    // not an envelope: show it as it is
  }
  return text;
}

/** A chat prompt's text (D82: `{"type": "chat.prompt", "turn_id", "text", "history"?}`), else undefined. */
function chatPromptText(text: string): string | undefined {
  try {
    const parsed: unknown = JSON.parse(text);
    if (parsed && typeof parsed === "object" && "type" in parsed && parsed.type === "chat.prompt" && "text" in parsed && typeof parsed.text === "string") {
      return parsed.text;
    }
  } catch {
    // a plain instruction
  }
  return undefined;
}
